import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useT } from "../i18n";
import {
  controlJob,
  dispatchJob,
  getJob,
  listFarmPrinters,
  updateJob,
  type FarmPrinter,
  type PrintJob,
} from "../api";
import { Icon } from "../components/Icon";
import { useToast } from "../lib/toast";
import { PartThumb } from "../scene/PartThumb";

const STATE: Record<string, { kind: string; ico: string; label: string }> = {
  queued: { kind: "", ico: "clock", label: "En file" },
  uploaded: { kind: "accent", ico: "wifi", label: "Envoyé" },
  printing: { kind: "warn", ico: "play", label: "Impression" },
  paused: { kind: "accent", ico: "pause", label: "Pause" },
  complete: { kind: "ok", ico: "check", label: "Terminé" },
  error: { kind: "bad", ico: "alert", label: "Erreur" },
};
const ORDER = ["queued", "uploaded", "printing", "complete"];

function fmtTime(s?: number | null): string {
  if (!s) return "--";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m.toString().padStart(2, "0")}m` : `${m}m`;
}

export function JobDetailPage() {
  const tr = useT();
  const { jobId = "" } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState<PrintJob | null>(null);
  const [printers, setPrinters] = useState<FarmPrinter[]>([]);
  const [target, setTarget] = useState("");
  const [err, setErr] = useState(false);
  const toast = useToast((s) => s.show);
  const timer = useRef<number | null>(null);

  const load = useCallback(() => {
    getJob(jobId).then(setJob).catch(() => setErr(true));
  }, [jobId]);

  useEffect(() => {
    load();
    listFarmPrinters().then((ps) => { setPrinters(ps); if (ps[0]) setTarget(ps[0].id); }).catch(() => {});
  }, [load]);

  async function dispatch() {
    if (!job || !target) return;
    const name = printers.find((p) => p.id === target)?.name ?? "l'imprimante";
    if (!window.confirm(`Lancer l'impression de « ${job.filename} » sur ${name} ? Cette action démarre une impression réelle.`)) return;
    try {
      await dispatchJob(job.id, { farm_printer_id: target, confirm: true });
      toast("Impression lancée sur l'imprimante");
      load();
    } catch (e) {
      toast(String((e as Error).message), "bad");
    }
  }
  async function control(action: "pause" | "resume" | "cancel") {
    if (!job) return;
    if (action === "cancel" && !window.confirm(`Annuler l'impression de « ${job.filename} » sur l'imprimante ?`)) return;
    try {
      await controlJob(job.id, { action, confirm: action === "cancel" });
      toast(action === "pause" ? "Impression en pause" : action === "resume" ? "Impression reprise" : "Impression annulée", action === "cancel" ? "bad" : "ok");
      load();
    } catch (e) {
      toast(String((e as Error).message), "bad");
    }
  }

  // Auto-refresh while the job is live.
  useEffect(() => {
    if (!job || ["complete", "error"].includes(job.state)) return;
    timer.current = window.setInterval(load, 5000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [job, load]);

  async function act(fn: () => Promise<unknown>, ok: string) {
    try { await fn(); toast(ok); load(); } catch (e) { toast(String((e as Error).message), "bad"); }
  }

  if (err) return <div className="muted">Job introuvable. <a style={{ color: "var(--accent-3)", cursor: "pointer" }} onClick={() => nav("/production")}>Retour</a>.</div>;
  if (!job) return <div className="muted">Chargement…</div>;
  const s = STATE[job.state] ?? STATE.queued;
  const curIdx = job.state === "error" ? ORDER.indexOf("printing") : ORDER.indexOf(job.state);

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            <a style={{ cursor: "pointer", color: "var(--muted)" }} onClick={() => nav("/production")}>PRODUCTION</a>
            <span style={{ margin: "0 7px", color: "var(--muted-2)" }}>/</span>
            <span className="id">{job.id.slice(0, 12)}</span>
          </div>
          <h1>{job.filename}</h1>
          <div className="sub">{job.printer_name}{job.material ? ` · ${job.material}` : ""}</div>
        </div>
        <div className="row">
          <span className={`badge ${s.kind}`}><Icon name={s.ico} size={12} />{tr(s.label)}</span>
          <button className="btn ghost sm" onClick={load}><Icon name="route" size={14} /> {tr("Rafraîchir")}</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 24, alignItems: "start" }}>
        <PartThumb material={job.material} passed={job.state === "complete"} size={150} />
        <div>
          {/* Timeline */}
          <div className="card" style={{ marginBottom: 18 }}>
            <div className="eyebrow" style={{ marginBottom: 14 }}>{tr("Étapes")}</div>
            <div className="row" style={{ alignItems: "flex-start", gap: 0 }}>
              {ORDER.map((st, i) => {
                const done = i < curIdx || job.state === "complete";
                const active = i === curIdx && job.state !== "complete";
                const failed = job.state === "error" && st === "printing";
                const color = failed ? "var(--bad)" : done ? "var(--ok)" : active ? "var(--warn)" : "var(--muted-2)";
                return (
                  <div key={st} style={{ flex: 1, textAlign: "center", position: "relative" }}>
                    {i < ORDER.length - 1 && <div style={{ position: "absolute", top: 13, left: "50%", right: "-50%", height: 2, background: done ? "var(--ok)" : "var(--line-2)" }} />}
                    <div style={{ width: 28, height: 28, borderRadius: 99, margin: "0 auto 8px", display: "grid", placeItems: "center", color, background: "var(--surface)", border: `2px solid ${color}`, position: "relative", zIndex: 1 }}>
                      <Icon name={failed ? "alert" : done ? "check" : STATE[st].ico} size={13} />
                    </div>
                    <div style={{ fontSize: 11.5, color, fontWeight: active ? 600 : 400 }}>{tr(failed ? "Erreur" : STATE[st].label)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Progress + metrics */}
          <div className="card" style={{ marginBottom: 18 }}>
            <div className="row between" style={{ marginBottom: 8 }}>
              <span className="muted" style={{ fontSize: 12.5 }}>Progression</span>
              <span className="num" style={{ fontSize: 13 }}>{Math.round((job.progress || 0) * 100)}%{job.eta && job.eta !== "--" ? ` · ETA ${job.eta}` : ""}</span>
            </div>
            <div className="progress" style={{ height: 9 }}><span style={{ width: `${Math.round((job.progress || 0) * 100)}%`, background: job.state === "error" ? "var(--bad)" : job.state === "complete" ? "var(--ok)" : "var(--warn)" }} /></div>
            <div className="row wrap" style={{ gap: 28, marginTop: 16 }}>
              <div><div className="eyebrow">Durée</div><div className="num" style={{ fontSize: 15, marginTop: 3 }}>{fmtTime(job.print_time_s)}</div></div>
              <div><div className="eyebrow">{tr("Matière")}</div><div className="num" style={{ fontSize: 15, marginTop: 3 }}>{job.material ?? "--"}</div></div>
              <div><div className="eyebrow">Filament {job.advisory ? "· estimé" : ""}</div><div className="num" style={{ fontSize: 15, marginTop: 3, color: job.advisory ? "var(--text-2)" : undefined }}>{job.filament_used_g != null ? `${Math.round(job.filament_used_g)} g` : "--"}</div></div>
              <div><div className="eyebrow">Coût {job.advisory ? "· estimé" : ""}</div><div className="num" style={{ fontSize: 15, marginTop: 3, color: job.advisory ? "var(--text-2)" : undefined }}>{job.cost != null ? `${job.cost.toFixed(2)} €` : "--"}</div></div>
              <div><div className="eyebrow">{tr("État")}</div><div style={{ fontSize: 15, marginTop: 3 }}>{tr(s.label)}</div></div>
            </div>
          </div>

          {/* Dispatch to a registered farm printer (real upload + start, behind confirm) */}
          <div className="card" style={{ marginBottom: 18 }}>
            <div className="eyebrow" style={{ marginBottom: 10 }}>{tr("Lancer sur la ferme")}</div>
            {job.dispatched_to ? (
              <div className="row wrap" style={{ gap: 8 }}>
                <span className="badge accent"><Icon name="wifi" size={11} /> {job.dispatched_to}</span>
                {!["complete", "error"].includes(job.state) && (
                  <>
                    {job.state === "paused"
                      ? <button className="btn sm" onClick={() => control("resume")}><Icon name="play" size={13} /> Reprendre</button>
                      : <button className="btn sm" onClick={() => control("pause")}><Icon name="pause" size={13} /> Pause</button>}
                    <button className="btn sm danger" onClick={() => control("cancel")}><Icon name="x" size={13} /> Annuler</button>
                  </>
                )}
              </div>
            ) : printers.length === 0 ? (
              <div className="muted" style={{ fontSize: 12.5 }}>{tr("Aucune imprimante enregistrée.")} <a style={{ color: "var(--accent-3)", cursor: "pointer" }} onClick={() => nav("/printers")}>{tr("Ajoute-en une dans la Ferme")}</a>.</div>
            ) : (
              <div className="row wrap" style={{ gap: 9, alignItems: "center" }}>
                <select value={target} onChange={(e) => setTarget(e.target.value)} style={{ fontSize: 12.5 }}>
                  {printers.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.kind})</option>)}
                </select>
                <button className="btn primary" disabled={!job.has_gcode} onClick={dispatch}>
                  <Icon name="play" size={14} /> Lancer l'impression
                </button>
                {!job.has_gcode && <span className="muted" style={{ fontSize: 11.5 }}><Icon name="alert" size={11} /> {tr("G-code requis : tranche le modèle d'abord.")}</span>}
              </div>
            )}
          </div>

          {/* Note + manual controls */}
          <div className="card">
            <div className="row between" style={{ marginBottom: 10 }}>
              <div className="eyebrow">{tr("Note · dev · amélioration")}</div>
              <button className="btn sm ghost" onClick={() => { const n = prompt("Note", job.note || ""); if (n != null) act(() => updateJob(job.id, { note: n }), "Note enregistrée"); }}><Icon name="edit" size={13} /> {tr("Éditer")}</button>
            </div>
            <div className="muted" style={{ fontSize: 13, minHeight: 22 }}>{job.note ? `📝 ${job.note}` : "Aucune note."}</div>
            <div className="row" style={{ gap: 7, marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line)" }}>
              {!["complete", "error"].includes(job.state) && <button className="btn sm" onClick={() => act(() => updateJob(job.id, { state: "printing" }), "Statut → Impression")}><Icon name="play" size={13} /> Impression</button>}
              {job.state === "printing" && <button className="btn sm" onClick={() => act(() => updateJob(job.id, { state: "complete", progress: 1 }), "Statut → Terminé")}><Icon name="check" size={13} /> Terminer</button>}
              <button className="btn sm danger" onClick={() => act(() => updateJob(job.id, { state: "error" }), "Marqué en erreur")}><Icon name="alert" size={13} /> Erreur</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
